// Data driven form component. Autosaving: there is no Save button -- every
// edit is persisted through the shared save queue (debounced for typed
// input, immediately for discrete controls), with save status reported by
// the global SaveStatusPill.
//
// item - item editing
// fields - map of fields: { name: key: type: }
// saveItem - async function to save the edited item; called once per autosave
//            flush with the built payload, so any post-save side effects
//            (list splices, parent state updates) run per successful save.
//            Must NOT close the hosting modal or navigate -- saves fire while
//            the user is still editing.
// deleteItem - optional async function to delete the item, null means no delete button.
// handleSecondaryButton - optional async function for a secondary button.
// secondaryButtonText - optional text for secondary button
// secondaryButtonVariant - type of secondary button (primary, secondary, danger, etc)
// deriveState - optional (previous, next) => state, to keep dependent fields in sync.
// validate - optional (formState) => {ok:true} | {ok:false, fieldErrors:{key:msg}};
//            invalid states are held (shown inline on the field), never saved.
// buildPayload - optional (formState, baseline) => payload, replacing the
//            default buildEditedFields diffing.
// recordKeyPrefix - autosave queue lane prefix; editors of the same record
//            must share it (e.g. 'task') so their writes serialize.
// autosave  - optional useAutosave(...) result, when a PARENT owns the form
//            state because it edits the same record through other controls
//            too (LogbookUpdateTask's radios). One controller means one edit
//            session and one baseline, so the two surfaces can't overwrite
//            each other -- sharing only the lane can't give you that, since
//            the queue keeps just the latest job. Supplying it makes
//            saveItem / validate / buildPayload / deriveState the parent's
//            job; the ones passed here are ignored (with a dev-only warning),
//            as is recordKeyPrefix -- the lane comes from the controller.
//            A parent that owns the controller also owns showing its
//            `fieldErrors`: this form is often a modal, and a held-invalid
//            record that only it can explain blocks the parent's saves too.
//            Whether you supply this is a decision per CALL SITE, not per
//            render: it selects between two component types, so flipping it
//            rebuilds the form from scratch and takes the user's in-progress
//            edit session with it. Hoist the condition to the parent that
//            decides whether to own a controller at all (dev-only warning).
// children  - optional children for the control.
//

import React, { useState, useEffect, useRef, useId } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useStatusMessage } from '.';
import TaskPogoDatePicker from './TaskPogoDatePicker';
import PlaceSuggestInput from './PlaceSuggestInput';
import TripSuggestInput from './TripSuggestInput';
import Confirm from './Confirm';
import { databaseDateTimeString, dateStringToDate } from '../fleet-shared/Common.mjs';
import { dateMidnight } from '../fleet-shared/TaskBuckets.mjs';
import { NewTaskName, NewItemName, NewCounterName, NewPlaceName } from '../utilities/Database.mjs';
import { buildEditedFields } from '../utilities/EditedFields.mjs';
import { useAutosave } from '../utilities/useAutosave';
import { saveQueue } from '../utilities/SaveQueue.mjs';


// One field in the form.
// `companion` is an optional boolean field rendered inline to the right of
// this field (e.g. "Include in Summary" next to Hours on Captain's Log).
//
// Exported (in addition to being used internally by EditForm below) so other
// always-editable, non-modal forms can render the same text/textarea/date/
// number/select/boolean/interval field UI without duplicating it -- see
// LogbookEntry.jsx's inline-editable field list.
//
// Autosave contract: `setState(newState, { immediate })` -- discrete controls
// (dates, checkboxes, selects, the interval unit) pass `immediate: true` so
// they persist on the spot; typed inputs (text/number/interval value) leave
// it false and ride the debounce. Callers whose setState ignores the second
// argument are unaffected. `error` renders an inline field-level message
// (autosave holds invalid states instead of saving them).
// `readOnly` (text/number inputs only) and `note` are for values a page
// derives rather than collects -- the trip page's Distance, which is computed
// from the route and rewritten on every map edit. The field still renders as
// itself, it just can't be typed into, and `note` says why.
//
// `field.suggestion` -- `{ value, label, title }` -- is a value the page can
// OFFER but must not assume, rendered as a button beside the input that writes
// it. The Start Counter carry-forward is the one caller (see
// LogbookTypes.mjs's `carryForwardEndCounter`). The split of duties is
// deliberate: THIS component owns the slot and the "only while empty" gate,
// because only it can see the live value -- the modal editors keep their form
// state inside EditForm, so a page has no way to ask. The page owns the value
// and the words, because only it knows where the number came from.
//
// It is a property of the FIELD rather than a prop of this component so it
// reaches the rows EditFormBody builds -- i.e. every ModalEditForm -- without
// new plumbing. Same reason `selects` and `loadSuggestions` ride there.
//
// `layout` picks the row geometry:
//   'inset' (default) - a right-aligned col-4 label beside a col-8 control,
//                       inside the field's own .container. Every form in the
//                       app looks like this.
//   'wide'            - a left-aligned label column beside the control, with
//                       no .container of its own (the caller supplies one).
//                       This exists because the trip page stacks these rows
//                       against TripWaypoints' list, whose Bootstrap grid runs
//                       edge to edge of the card; 'inset' rows next to it read
//                       as a different form. The label column width here
//                       (col-12 col-sm-3 col-lg-2) is TripWaypoints' -- the two
//                       have to agree or the labels don't line up. Don't
//                       "simplify" the two layouts back into one without
//                       restyling every other form to match.
export const Field = ( {field, companion, state, setState, setFocus, inputRef, error, readOnly, note, layout = 'inset'} ) => {
  // Unique id per field instance so the visible <label> can be associated
  // with its control (htmlFor/id). This is what gives each input an
  // accessible name -- the placeholders were removed, and without this the
  // inputs would have no label at all.
  const fieldId = useId();
  const companionId = useId();

  // For interval fields, track the chosen unit (days/months) locally so the
  // toggle "sticks" even when the numeric value is blank. Deriving the unit
  // purely from state would snap the dropdown back to "days" the moment the
  // value is cleared, making it impossible to pick "months" before typing.
  const [intervalUnit, setIntervalUnit] = useState(() => {
    if (field.type !== 'interval' || state == null) return 'days';
    const months = state[field.monthsKey];
    return (months !== null && months !== undefined && months !== '' && months > 0)
      ? 'months'
      : 'days';
  });

  // In the DB, dates are stored in an ISO 8601 Date string ("YYYY-MM-DD").
  // The DatePicker control takes a Javascript Date object, translate to/from that.
  function dateFieldTextToDate(key) {
    if (state) {
      let dateString = state[key];
      return dateStringToDate(dateString);
    }
    return null;
  }

  function dateFieldChanged(key, date) {
    var newDate = null;
    if ((date === undefined) || (date === null) || (date === "")) {
      newDate = null;
    } else {
      // Store date-only (normalized to local start-of-day); we no longer keep a time of day.
      newDate = databaseDateTimeString(dateMidnight(date));
    }

    // Update UI. Picking a date is a discrete action -- save immediately.
    let newState = {...state};
    newState[key] = newDate;
    setState(newState, { immediate: true });
  }

  // Given an ID from a select, find it and return the name.
  function selectValueFromId(field, id) {
    for (var i=0; i < field.selects.length; i++) {
      if (field.selects[i].id === id) {
        return field.selects[i].name;
      }
    }
    return null;
  }

    // Given a value from a select, find it and return the id.
  function selectIdFromValue(field, value) {
    for (var i=0; i < field.selects.length; i++) {
      if (field.selects[i].name === value) {
        return field.selects[i].id;
      }
    }
    // This shouldn't happen
    console.log('Bad value? ' + value);
    return null;
  }


  // Converts the input, which if often a string to the correct type in our model.
  function coerceType(field, value) {
    // Empty? Return null.
    if ((null === value) || ("" === value)) {
      // Amplify's graphql API (DynamoDB?): if it's used in an index as a sortKeyField, it cannot be null or empty.
      // So, if it's empty return a space.
      if (field.isSortField === true) {
        return ' ';
      }
      return null;
    }

    // `place` and `trip` share the text branch: a place name and a trip name
    // are strings like any other, only their INPUT differs. (A trip field's
    // second key, the id it binds to, is not coerced here -- it never passes
    // through a field's own value.)
    switch (field.type) {
    case 'text':
    case 'place':
    case 'trip':
    default:
      // Turn in to a string and trim it.
      const trimmed = String(value);
      // *** I decided to not trim, as editing notes wouldn't add spaces.
      // String(value).trim();

      // Empty string?
      if (trimmed.length === 0) {
        // Amplify's graphql API (DynamoDB?): if it's used in an index as a sortKeyField, it cannot be null or empty.
        // So, if it's empty return a space.
        if (field.isSortField === true) {
          return ' ';
        }

        // Don't put into database...
        return null;
      }

      return trimmed;
    case 'date':
      return value; 
    case 'number':
      return parseFloat(value);
    case 'boolean':
      return Boolean(value);
    case 'select':
      return selectIdFromValue(field, value);
    }
  }

  // Given an "id" for a select, return the "name" from the item's selects.
  function selectDefaultValue(field) {
    let value = state[field.key];
    return selectValueFromId(field, value);
  }

  function handleChange(field, event) {
    // preventDefault on a checkbox click cancels the toggle in some browsers.
    if (event.target.type !== 'checkbox') {
      event.preventDefault();
    }

    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    let newState = {...state};
    newState[field.key] = coerceType(field, value);

    // Discrete controls save immediately; typed input rides the debounce.
    const immediate =
      target.type === 'checkbox' ||
      field.type === 'boolean' ||
      field.type === 'select';
    setState(newState, { immediate });
  }

  function outputOption(option) {
    return (
      <option key={option.index} value={option.name}>{option.name}</option>
    );
  }

  // Interval fields ("every N days" / "every N months") store their value in
  // one of two underlying keys -- `field.key` (days) or `field.monthsKey`
  // (months) -- with only one set at a time. These helpers read/write that
  // pair as a single number + unit control.
  function intervalCurrentValue(field) {
    if (state == null) return '';
    const key = intervalUnit === 'months' ? field.monthsKey : field.key;
    const value = state[key];
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
    return '';
  }

  function intervalCurrentUnit() {
    return intervalUnit;
  }

  function setIntervalState(field, rawValue, unit, { immediate = false } = {}) {
    let num = null;
    if (rawValue !== '' && rawValue !== null && rawValue !== undefined) {
      const parsed = parseFloat(rawValue);
      num = isNaN(parsed) ? null : parsed;
    }

    let newState = {...state};
    if (unit === 'months') {
      newState[field.monthsKey] = num;
      newState[field.key] = null;
    } else {
      newState[field.key] = num;
      newState[field.monthsKey] = null;
    }
    setState(newState, { immediate });
  }

  function intervalValueChanged(field, event) {
    event.preventDefault();
    setIntervalState(field, event.target.value, intervalUnit);
  }

  function intervalUnitChanged(field, event) {
    const unit = event.target.value;
    // Carry the current value over to the newly-selected unit's key before
    // updating the local unit, so switching days <-> months keeps the number.
    // The unit dropdown is a discrete control -- save immediately.
    setIntervalState(field, intervalCurrentValue(field), unit, { immediate: true });
    setIntervalUnit(unit);
  }

  // If it's a text field and has a default value for when an item is created, select all when it gets focus.
  // So, when I create a new object I give it a name like 'New Item'. When the user taps on name field, I want
  // to select all so they can type over it.
  const handleFocus = (event) => {
    if (
      (event.target.value === NewTaskName) ||
      (event.target.value === NewItemName) ||
      (event.target.value === NewCounterName) ||
      (event.target.value === NewPlaceName)
    ) {
      event.target.select();
    }
  }


  // Get the value of a field from the state.
  function fieldValue(field) {
    // Make sure we have a state and the field is in it. For sortKeyFields, I use a space to represent
    // an empty string in the database, so if I've done that, drop thru and return "".
    // Note: explicitly check null/undefined rather than truthiness so a real
    // numeric `0` (e.g. counter at zero) renders as "0" instead of being
    // swallowed as blank -- otherwise the user can't tell 0 apart from "no
    // value" in the edit form.
    if (state == null) return "";
    const value = state[field.key];
    if (value === null || value === undefined || value === ' ') {
      return "";
    }
    return value;
  }


  // The 'wide' layout puts the control inside its own grid column, so the
  // row-relative col-8/col-2 widths below can't carry over -- the control
  // sizes itself within that column instead.
  const isWide = layout === 'wide';

  // d-flex needed for justify-content- and align-items-
  // justify-content-start to left justify
  const InputFieldClass = isWide ? 'w-100' : 'col-8 d-flex justify-content-start'
  const ShortFieldClass = isWide ? '' : 'col-2 d-flex justify-content-start'
  // When a companion checkbox sits beside a short number input, drop the
  // bootstrap column class so the pair can live in one flex row.
  const CompanionNumberClass = 'd-flex justify-content-start'
  const CompanionNumberStyle = { width: '6rem' };
  // Wide rows have no col-2 to inherit a width from, so short controls take
  // the companion input's fixed width rather than filling the column.
  const ShortFieldStyle = isWide ? CompanionNumberStyle : undefined;
  // Interval pairs a short number input with a unit dropdown. The wrapper
  // restores vertical alignment and spacing; the input uses `w-25` inside
  // `col-8` so its width matches a row-level `col-2` (2/12 of the row).
  const IntervalControlsClass = isWide
    ? 'd-flex justify-content-start align-items-center gap-2'
    : 'col-8 d-flex justify-content-start align-items-center gap-2'
  const IntervalInputClass = isWide ? '' : 'w-25'
  const IntervalInputStyle = isWide ? CompanionNumberStyle : undefined;

  // Companion boolean (e.g. Include in Summary). Missing/null defaults to the
  // field's `default` so legacy rows render as checked when default is true.
  function companionChecked(companionField) {
    if (state == null) return companionField.default !== false;
    const value = state[companionField.key];
    if (value === null || value === undefined) {
      return companionField.default !== false;
    }
    return value !== false;
  }

  // Is the suggestion actually on screen? It is offered only while the field is
  // empty: it hands over a value the user hasn't got, and once they have one --
  // including a genuine 0, which fieldValue deliberately reports as set --
  // there is nothing to offer.
  //
  // This is what `inline` below keys off, NOT the mere presence of
  // `field.suggestion`. The suggestion is attached by the page whenever a prior
  // entry has a value, whether or not this field is empty, so testing for it
  // would widen a FILLED Start Counter on an entry that happens to have an
  // eligible predecessor and leave the same filled field narrow on the
  // logbook's first entry -- two identical-looking rows at two widths, decided
  // by a button neither of them shows.
  const showSuggestion = Boolean(field.suggestion) && fieldValue(field) === '';

  // A second control shares the row -- the companion checkbox, or a suggestion
  // button. Either way the input can no longer carry a row-level bootstrap
  // column, because it is about to be nested one level deeper (a `col-2` inside
  // a `col-8` is 2/12 of the WRAPPER, not of the row). Both the wrapper choice
  // below and the number field's width key off this one flag.
  const inline = Boolean(companion) || showSuggestion;

  // Writing is a discrete action, so it saves on the spot rather than riding
  // the typing debounce, and it goes through the UPDATER form because the
  // `state` this closed over is last render's.
  function outputSuggestion() {
    if (!showSuggestion) return null;
    const suggestion = field.suggestion;
    return (
      <Button
        variant="link"
        size="sm"
        className="p-0 ms-2 small text-decoration-none text-nowrap"
        title={suggestion.title}
        onClick={() => setState(
          (current) => ({ ...current, [field.key]: suggestion.value }),
          { immediate: true }
        )}
      >
        {suggestion.label}
      </Button>
    );
  }

  function outputCompanionCheckbox(companionField) {
    if (!companionField) return null;
    return (
      <Form.Check
        type="checkbox"
        id={companionId}
        className="ms-3 mb-0"
        label={companionField.name}
        checked={companionChecked(companionField)}
        onChange={(event) => handleChange(companionField, event)}
      />
    );
  }

  // This returns the HTML for an input field.
  function outputInputField(field) {
    switch (field.type) {
    case 'date':
      return (
        <TaskPogoDatePicker
          // `inputRef` is only defined for the first field; passing it here
          // lets ModalEditForm.onEntered hand the mobile-keyboard focus off
          // to the date input (via its `setFocus()`) when a form's first
          // field is a date -- e.g. logbook entries (`completedTaskFields`
          // starts with `{ key: 'date', type: 'date' }`).
          ref={inputRef}
          id={fieldId}
          className={InputFieldClass}
          // react-datepicker wraps the input in an inline-block div, so `w-100`
          // on the input alone would only fill that shrink-wrapped wrapper.
          wrapperClassName={isWide ? 'w-100' : undefined}
          selected={dateFieldTextToDate(field.key)}
          onChange={(date) => dateFieldChanged(field.key, date)}
          dateFormat="MMM d, yyyy"
          isClearable
          placeholderText="mm/dd/yyyy"
        />
      );

    // Numbers have a step, and optionally a floor. `min` only sets the spinner's
    // floor and the mobile keypad -- typing still reaches coerceType's parseFloat
    // unchecked, so a field that really has a lower bound needs `validate` too.
    case 'number': {
      const numberClass = inline ? CompanionNumberClass : ShortFieldClass;
      const numberStyle = inline ? CompanionNumberStyle : ShortFieldStyle;
      if (setFocus) {
        return (
          <input 
            ref={inputRef}
            id={fieldId}
            className={numberClass}
            style={numberStyle}
            name={field.name}
            type={field.type}
            step={field.step ?? 1}
            min={field.min}
            placeholder={field.placeholder}
            value={fieldValue(field)}
            readOnly={Boolean(readOnly)}
            autoFocus
            onChange={event => {
              handleChange(field, event)
            }} />
        );
      } else {
        return (
          <input
            id={fieldId}
            className={numberClass}
            style={numberStyle}
            name={field.name}
            type={field.type}
            step={field.step ?? 1}
            min={field.min}
            placeholder={field.placeholder}
            value={fieldValue(field)}
            readOnly={Boolean(readOnly)}
            onChange={event => {
              handleChange(field, event)
            }} />
        );
      }
    }

    case 'boolean':
      return (
        <Form.Check
          type="checkbox"
          id={fieldId}
          className={isWide ? 'd-flex align-items-center mb-0' : 'col-8 d-flex align-items-center mb-0'}
          label={field.name}
          checked={companionChecked(field)}
          onChange={(event) => handleChange(field, event)}
        />
      );

    case 'interval':
      return (
        <div className={IntervalControlsClass}>
          <input
            id={fieldId}
            className={IntervalInputClass}
            style={IntervalInputStyle}
            name={field.name}
            type="number"
            step={1}
            min={0}
            value={intervalCurrentValue(field)}
            onChange={event => intervalValueChanged(field, event)}
          />
          <select
            name={`${field.name} unit`}
            value={intervalCurrentUnit(field)}
            onChange={event => intervalUnitChanged(field, event)}
          >
            <option value="days">Days</option>
            <option value="months">Months</option>
          </select>
        </div>
      );

    case 'select':
      return (
        <select 
          id={fieldId}
          name={field.name}
          className={ShortFieldClass}
          style={ShortFieldStyle}
          defaultValue={selectDefaultValue(field)}
          onChange={event => { 
            handleChange(field, event) 
          }}>
            {
              field.selects.map((option, index) => outputOption(option))
            }
        </select>
      );

    // Multi-line text. Deliberately shares `text`'s save contract: handleChange
    // only sets `immediate` for checkbox targets, `boolean` and `select`, so a
    // textarea rides the debounce and flushes on the container's blur (focusout
    // bubbles from a textarea too). A notes field that fired a mutation per
    // keystroke would be the worst offender in the app.
    //
    // Uses InputFieldClass, not form-control: no other input in this form
    // carries Bootstrap's control styling, and one row that did would read as a
    // different form.
    case 'textarea':
      return (
        <textarea
          ref={setFocus ? inputRef : undefined}
          id={fieldId}
          className={InputFieldClass}
          name={field.name}
          rows={field.rows ?? 3}
          value={fieldValue(field)}
          readOnly={Boolean(readOnly)}
          autoFocus={setFocus ? true : undefined}
          onFocus={handleFocus}
          onChange={event => {
            handleChange(field, event)
          }} />
      );

    // A name from the shared gazetteer (TaskPogoPlace): a From/To on a
    // Captain's / Pilot's entry, anywhere else a place is named. Stored rows
    // suggest first, geocoder candidates behind them.
    //
    // A pick commits the BARE NAME and nothing else -- no coordinates, no new
    // gazetteer row. TripWaypoints records a picked place because the waypoint
    // takes its position; here nothing does, and `suggestPlaces` reads the
    // whole table, so a gazetteer that stored every name anyone ever typed
    // into a text box would steadily degrade its own suggestions.
    case 'place':
      return (
        <div className={InputFieldClass}>
          <PlaceSuggestInput
            id={fieldId}
            value={fieldValue(field)}
            ariaLabel={field.name}
            placeholder={field.placeholder}
            onChange={(text) => setState({ ...state, [field.key]: coerceType(field, text) })}
            // A pick is a discrete gesture on a menu, like a date pick -- it
            // shouldn't sit in the typing debounce waiting for a keystroke
            // that isn't coming.
            onPick={(place) =>
              setState({ ...state, [field.key]: coerceType(field, place.name) }, { immediate: true })
            }
          />
        </div>
      );

    // The Trip Name on a leg or a Captain's-Log entry: a name to type, plus a
    // menu of the trips that already exist so this record can be filed under
    // one of them. See TripSuggestInput -- the id it writes is what identifies
    // a trip now, which is what lets two of them be called "Day".
    //
    // The one thing that differs from every other case here: this input owns
    // TWO keys, its own and `field.idKey`. Both are written in a single
    // setState, and through the UPDATER form rather than a `{...state}`
    // spread -- a pick can follow a keystroke inside one tick, and the `state`
    // this closure captured is last render's.
    case 'trip': {
      const writeTrip = (trip, meta) =>
        setState(
          (current) => ({
            ...current,
            [field.key]: coerceType(field, trip.name),
            ...(field.idKey ? { [field.idKey]: trip.tripGroupId ?? null } : {}),
          }),
          meta
        );
      return (
        <div className={InputFieldClass}>
          <TripSuggestInput
            id={fieldId}
            value={fieldValue(field)}
            tripGroupId={field.idKey ? (state?.[field.idKey] ?? null) : null}
            ariaLabel={field.name}
            placeholder={field.placeholder}
            loadSuggestions={field.loadSuggestions}
            autoFocus={Boolean(setFocus)}
            inputRef={inputRef}
            onChange={(trip) => writeTrip(trip)}
            // A pick is a discrete gesture on a menu, like a date pick -- it
            // shouldn't sit in the typing debounce waiting for a keystroke
            // that isn't coming.
            onPick={(trip) => writeTrip(trip, { immediate: true })}
          />
        </div>
      );
    }

    // Default is text.
    case 'text':
    default:
      if (setFocus) {
        return (
          <input 
            ref={inputRef}
            id={fieldId}
            className={InputFieldClass}
            name={field.name}
            type={field.type}
            value={fieldValue(field)}
            readOnly={Boolean(readOnly)}
            autoFocus
            onFocus={handleFocus}
            onChange={event => {
              handleChange(field, event)
            }} />
        );
      } else {
        return (
          <input
            id={fieldId}
            className={InputFieldClass}
            name={field.name}
            type={field.type}
            value={fieldValue(field)}
            readOnly={Boolean(readOnly)}
            onFocus={handleFocus}
            onChange={event => {
              handleChange(field, event)
            }} />
        );
      }
    }
  }

  // Wide rows: the label sits in its own left column and the control fills the
  // rest, so note/error go under the control (the way a waypoint row's
  // coordinates sit under its name box) rather than in a spacer column.
  if (isWide) {
    return (
      <div className="row g-2 align-items-center py-2">
        <div className="col-12 col-sm-3 col-lg-2">
          <label htmlFor={fieldId} className="form-label mb-0">{field.name}</label>
        </div>
        <div className="col-12 col-sm d-flex flex-column align-items-start">
          <div className="d-flex flex-row align-items-center w-100">
            {outputInputField(field)}
            {companion && outputCompanionCheckbox(companion)}
            {outputSuggestion()}
          </div>
          {error && (
            <div className="text-danger small mt-1" role="alert">{error}</div>
          )}
          {note && !error && (
            <div className="text-body-secondary small mt-1">{note}</div>
          )}
        </div>
      </div>
    );
  }

  // d-flex is essential in order for justify-content- and align-items- to work
  // justify-content-end moves your labels to the right
  // align-items-end moves your labels down so that they're aligned with forms
  //pe-3 sets padding-right
  return (
    <div className="container">
      <div className="d-flex flex-row align-items-center">
        <label htmlFor={fieldId} className="col-4 d-flex justify-content-end align-items-center pe-3">{field.name}</label>
        {inline ? (
          <div className="col-8 d-flex justify-content-start align-items-center">
            {outputInputField(field)}
            {outputCompanionCheckbox(companion)}
            {outputSuggestion()}
          </div>
        ) : (
          outputInputField(field)
        )}
      </div>
      {error && (
        <div className="d-flex flex-row">
          <div className="col-4" aria-hidden="true"></div>
          <div className="col-8 text-danger small" role="alert">{error}</div>
        </div>
      )}
      {note && !error && (
        <div className="d-flex flex-row">
          <div className="col-4" aria-hidden="true"></div>
          <div className="col-8 text-body-secondary small">{note}</div>
        </div>
      )}
    </div>
  );
}

// Re-exported (like `Field` above) so always-editable pages that render
// `Field` rows themselves -- Task.jsx, Trip.jsx -- build byte-identical
// payloads to the modal editors instead of re-deriving these rules. The
// implementation lives in utilities/EditedFields.mjs so non-component code
// (useAutosave) can import it without pulling in component modules.
export { buildEditedFields };

// The autosave lane an editor of `item` writes on. Exported so a parent that
// owns the controller (see `autosave` below) can name the same lane.
export function recordKeyFor(recordKeyPrefix, item) {
  return `${recordKeyPrefix || 'record'}:${item && item.id != null ? item.id : 'new'}`;
}

// The form itself, given an autosave controller. Split from `EditForm` only so
// the controller can come from either place without a conditional hook.
const EditFormBody = ({
  item,
  fields,
  deleteItem,
  handleSecondaryButton = null,
  secondaryButtonText = 'Cancel',
  secondaryButtonVariant = 'secondary',
  children,
  firstInputRef,
  recordKeyPrefix, // default lives in recordKeyFor, which other modules call too
  autosave,
}) => {
  const { formState, setFormState, fieldErrors, flush } = autosave;

  // The controller's own lane, not one re-derived from `recordKeyPrefix`. With
  // a shared controller the two can disagree, and a delete that cancels the
  // wrong lane leaves the real one live against the row it just removed.
  const recordKey = autosave.recordKey || recordKeyFor(recordKeyPrefix, item);

  // Watch for when unmounted, so don't do things if this object is discarded
  // during an async operation.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    }
  }, [])

  // Flush on unmount -- closing a modal has to persist what's in the debounce
  // window. `useAutosave` does this for a controller it owns, but a shared one
  // outlives this form (it belongs to the page behind the modal), so the form
  // has to ask on its own way out.
  //
  // In the own-controller case that makes two flushes of the same lane (this
  // cleanup, then useAutosave's). Left alone deliberately: saveQueue.flush is
  // idempotent -- it returns early once the job is in flight -- so the second
  // is a no-op, and suppressing it would mean threading an `ownsController`
  // prop down here purely to skip nothing.
  useEffect(() => () => flush(), [flush]);

  // Loading/error UI -- for the delete flow only. Save feedback comes from
  // the global SaveStatusPill via the autosave queue.
  const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();

  // Confirm delete
  const [showConfirm, setShowConfirm] = useState(false);

  // When delete tapped, show confirm.
  function handleDelete() {
    setShowConfirm(true);
  }


  // Delete was confirmed, ask the parent to delete us. Cancel any pending or
  // in-flight save for this record first -- a late update against a deleted
  // row must neither error nor resurrect data.
  async function handleDeleteConfirmed() {
    showText("Deleting");

    try {
      setShowConfirm(false);
      saveQueue.cancel(recordKey);
      await deleteItem();
      if (mounted.current) {
        hideStatus()
      }
    } catch (err) {
      showError(err);
    }
  }

  function secondaryButton() {
    hideStatus();
    if (handleSecondaryButton) {
      handleSecondaryButton();
    }
  }

  // Inline, NOT a `const DeleteButton = () => ...` built during render: a
  // component defined while rendering is a new type on every render, so React
  // tears its subtree down and rebuilds it rather than updating it. That put
  // the open Confirm dialog through a full unmount/remount every time the form
  // re-rendered -- which under autosave is routine, since the save status
  // changes while the user is looking at the dialog. The dialog replayed its
  // entrance animation, dropped focus, and handed tests a detached node.
  const deleteControls = deleteItem ? (
    <>
      <Button variant="danger" className="mr-2 col-3" style={{float:'right'}} onClick={handleDelete}>
          Delete
      </Button>
      <Confirm
        show={showConfirm}
        onConfirm={handleDeleteConfirmed}
        onHide = {() => setShowConfirm(false)}
        body='Are you sure?'
        confirmText='Confirm Delete'
        title={`Delete`}>
      </Confirm>
    </>
  ) : null;

  // Fields marked `inlineWith` render as a companion on another field's row
  // (e.g. Include in Summary next to Hours), not as their own row.
  const visibleFields = fields.filter((f) => !f.inlineWith);

  // Leaving any field flushes the debounce -- onBlur bubbles up from the
  // inputs, so one handler on the container covers every field.
  return (
    <Form onSubmit={(event) => event.preventDefault()} onBlur={() => flush()}>
      {
        visibleFields.map((field, index) => {
          const companion = fields.find((f) => f.inlineWith === field.key) || null;
          return (
            <Field
              key={`${item ? item.id ?? 'new' : 'new'}:${field.key}`}
              field={field}
              companion={companion}
              state={formState}
              setState={setFormState}
              setFocus={index===0}
              inputRef={index===0 ? firstInputRef : undefined}
              error={fieldErrors[field.key]}
            />
          );
        })
      }

      {children}

      <StatusMessage/>

      <div className="container m-2">
        { handleSecondaryButton && (
          <Button variant={secondaryButtonVariant} className="col-3" onClick={secondaryButton}>
            {secondaryButtonText}
          </Button>

          )
        }

        {deleteControls}
      </div>
    </Form>
  );
}

// The ordinary case: this form is the only editor of the record, so it owns
// the autosave controller.
const EditFormOwnAutosave = (props) => {
  const { item, fields, saveItem, buildPayload, validate, deriveState, recordKeyPrefix } = props;

  // Every field edit is pushed through the shared save queue -- debounced for
  // typed input, immediately for discrete controls -- with the consumer's
  // `saveItem` callback as the save function, so its post-save side effects
  // (list splices, parent updates) run per successful flush. The record key is
  // shared with any other editor of the same record (prefix + id) so
  // concurrent edits serialize through one lane.
  const autosave = useAutosave({
    recordKey: recordKeyFor(recordKeyPrefix, item),
    original: item,
    fields,
    buildPayload,
    validate,
    save: saveItem,
    deriveState,
  });

  return <EditFormBody {...props} autosave={autosave} />;
};

// Props the autosave controller owns. When a parent supplies `autosave` these
// are ignored -- silently, which is exactly how a wrong-behaviour bug hides, so
// say so in development.
const OWNED_BY_CONTROLLER = ['saveItem', 'validate', 'buildPayload', 'deriveState'];

// Two things about `autosave` that only show up at runtime. Both are dev-only:
// neither has an observable other than the console, and asserting on console
// output is an anti-pattern here (CLAUDE.md), so neither is tested.
function useAutosavePropWarnings(autosave, props) {
  const ignored = autosave
    ? OWNED_BY_CONTROLLER.filter((name) => props[name] !== undefined).join(', ')
    : '';
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !ignored) return;
    console.warn(
      `EditForm: ignoring ${ignored} -- an \`autosave\` controller was supplied, ` +
      'so its owner supplies those. Move them to that useAutosave() call.'
    );
  }, [ignored]);

  // Supplying `autosave` picks a different component type, so a call site that
  // supplies it conditionally makes React tear the form down and rebuild it --
  // silently discarding formState, fieldErrors and an open delete dialog, i.e.
  // whatever the user was in the middle of. Nothing about the resulting bug
  // points back here, so name it.
  const suppliedAtFirstRender = useRef(Boolean(autosave));
  const supplied = Boolean(autosave);
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (supplied === suppliedAtFirstRender.current) return;
    console.warn(
      'EditForm: the `autosave` prop appeared or disappeared after the first ' +
      'render. That swaps the form for a different component type, so React ' +
      'rebuilds it and the in-progress edit session is lost. Decide once, per ' +
      'call site -- hoist the condition to whoever owns the controller.'
    );
  }, [supplied]);
}

export const EditForm = ({ autosave, ...props }) => {
  useAutosavePropWarnings(autosave, props);
  return autosave
    ? <EditFormBody {...props} autosave={autosave} />
    : <EditFormOwnAutosave {...props} />;
};


export default EditForm;
